import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Search,
  SlidersHorizontal,
  Wallet,
  X,
} from "lucide-react";
import { toast } from "react-toastify";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import CardHeader from "src/components/common/card/CardHeader";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Skeleton from "src/components/ui/Skeleton";
import { Badge } from "src/components/ui/Badge";
import paymentApis from "src/api/payments";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import {
  PAYMENT_METHOD_LABELS,
  paymentMethodLabel,
  viaLabel,
} from "src/components/payments/paymentLabels";
import { PaymentsSummary } from "src/types/salePayment.types";

const METHOD_OPTIONS = Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const SOURCE_OPTIONS = [
  { value: "online", label: "Online (link)" },
  { value: "offline", label: "Offline" },
];

const STATUS_OPTIONS = [
  { value: "success", label: "Received" },
  { value: "refund_due", label: "Refund due" },
  { value: "refunded", label: "Refunded" },
];

const PaymentsListPage: React.FC = () => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("");
  const [via, setVia] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(10);

  const [rows, setRows] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<PaymentsSummary | null>(null);

  useEffect(() => {
    paymentApis
      .getSummary()
      .then((res: any) => setSummary(res?.data || null))
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const getList = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        const res: any = await paymentApis.getPaymentsList({
          page,
          limit,
          ...(search ? { search } : {}),
          ...(method ? { method } : {}),
          ...(via ? { via } : {}),
          ...(source ? { source } : {}),
          ...(status ? { status } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        });
        setRows(res?.data || []);
        setPagination(res?.pagination || null);
      } catch (error: any) {
        toast.error(error.message || "Failed to load payments");
      } finally {
        setLoading(false);
      }
    },
    [limit, search, method, via, source, status, from, to]
  );

  useEffect(() => {
    getList(1);
  }, [getList]);

  const hasActiveFilters = !!(search || method || via || source || status || from || to);
  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setMethod("");
    setVia("");
    setSource("");
    setStatus("");
    setFrom("");
    setTo("");
  };

  const stats = [
    {
      label: "Collected this month",
      value: summary && currencyFormatter(summary.collectedThisMonth),
      sub: summary && `${currencyFormatter(summary.collectedToday)} today`,
    },
    {
      label: "Outstanding",
      value: summary && currencyFormatter(summary.outstandingAmount),
      sub: summary && `${summary.outstandingOrders} orders with balance due`,
    },
    {
      label: "Active payment links",
      value: summary && String(summary.activeLinksCount),
      sub: summary && `${currencyFormatter(summary.activeLinksAmount)} awaiting payment`,
    },
    {
      label: "Links expired",
      value: summary && String(summary.expiredLinkOrders),
      sub: "orders still unpaid",
    },
  ];

  return (
    <DashboardLayout
      meta={{
        title: "Payments | Invyntra",
        description: "Every payment received against sale orders.",
        keywords: "payments, collections, receivables, invyntra",
      }}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Wallet size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Payments</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Every payment received against sale orders — online and offline.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {stat.label}
              </div>
              {summary ? (
                <>
                  <div className="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{stat.sub}</div>
                </>
              ) : (
                <div className="mt-2 space-y-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              )}
            </Card>
          ))}
        </div>

        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Payment ID, order ID, reference..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>
              <FilterSelect
                variant="boxed"
                label="Method"
                value={method}
                onChange={setMethod}
                allLabel="All methods"
                options={METHOD_OPTIONS}
              />
              <FilterSelect
                variant="boxed"
                label="Paid via"
                value={via}
                onChange={setVia}
                allLabel="Any app / bank / card"
                options={(summary?.vias || []).map((v) => ({ value: v, label: viaLabel(v) }))}
              />
              <FilterSelect
                variant="boxed"
                label="Source"
                value={source}
                onChange={setSource}
                allLabel="Online & offline"
                options={SOURCE_OPTIONS}
              />
              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={setStatus}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
              <Input
                label="From"
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
              />
              <Input
                label="To"
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
              />
              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <CardHeader
            title="Payments received"
            description="Newest first"
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />
          <TableV2
            columns={[
              {
                key: "paidAt",
                header: "Date",
                accessor: ({ row }) => formatDateTime(row.paidAt, "DD MMM YYYY, hh:mm A"),
              },
              {
                key: "paymentId",
                header: "Payment ID",
                accessor: ({ row }) => (
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {row.paymentId}
                  </span>
                ),
              },
              {
                key: "orderCode",
                header: "Order",
                accessor: ({ row }) => (
                  <Link
                    to={`/app/sale/${row.referenceId?._id || row.referenceId}`}
                    className="text-primary-600 hover:underline dark:text-primary-400"
                  >
                    {row.orderCode}
                  </Link>
                ),
              },
              {
                key: "customer",
                header: "Customer",
                accessor: ({ row }) =>
                  typeof row.customerId === "object" ? row.customerId?.name || "-" : "-",
              },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ row }) => (
                  <span className="font-semibold">{currencyFormatter(row.amount)}</span>
                ),
              },
              {
                key: "method",
                header: "Method",
                accessor: ({ row }) => paymentMethodLabel(row),
              },
              {
                key: "source",
                header: "Source",
                accessor: ({ row }) => (row.source === "online" ? "Online (link)" : "Offline"),
              },
              {
                key: "reference",
                header: "Reference",
                accessor: ({ row }) => (
                  <span className="font-mono text-xs">
                    {row.providerPaymentId || row.referenceNo || "-"}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => {
                  const refunded = row.refundedAmount || 0;
                  // Money still held against an order that was cancelled.
                  const refundDue =
                    row.status === "success" &&
                    row.referenceId?.orderStatus === "cancelled" &&
                    row.amount - refunded > 0;
                  if (row.status === "refunded") return <Badge text="Refunded" color="gray" />;
                  if (refundDue) return <Badge text="Refund due" color="red" />;
                  if (refunded > 0) return <Badge text="Partly refunded" color="yellow" />;
                  return <Badge text="Received" color="green" />;
                },
              },
            ]}
            data={rows}
            pagination={pagination}
            onPageChange={getList}
            loading={loading}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentsListPage;
